import { Slot } from "@radix-ui/react-slot";
import clsx from "clsx";
import { forwardRef, HTMLAttributes } from "react";
import { SpacingVariants } from "../../../types/spacing";
import classes from "./flex.module.css";

export type FlexContainerProps = HTMLAttributes<HTMLDivElement> & {
  asChild?: boolean;
  column?: boolean;
  columnBelowWidth?: string;
  justify?: "start" | "center" | "end" | "space-between";
  align?: "start" | "center" | "end";
  gap?: SpacingVariants;
  rowGap?: SpacingVariants;
  columnGap?: SpacingVariants;
  wrap?: boolean;
};

export const Container = forwardRef<HTMLDivElement, FlexContainerProps>(
  (
    {
      asChild = false,
      column = false,
      wrap = false,
      columnBelowWidth,
      gap,
      rowGap,
      columnGap,
      justify,
      align,
      style,
      className,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "div";
    const inlineStyle = {
      ...(gap ? { gap: `var(--spacing-${gap})` } : null),
      ...(rowGap ? { rowGap: `var(--spacing-${rowGap})` } : null),
      ...(columnGap ? { columnGap: `var(--spacing-${columnGap})` } : null),
      ...(columnBelowWidth ? { "--column-threshold": columnBelowWidth } : null),
      ...style,
    };

    return (
      <Comp
        ref={ref}
        className={clsx(
          classes.container,
          column && classes.column,
          columnBelowWidth && classes.columnBelowWidth,
          justify && classes[`justify-${justify}`],
          align && classes[`align-${align}`],
          wrap && classes.wrap,
          className,
        )}
        style={inlineStyle}
        {...props}
      />
    );
  },
);

Container.displayName = "FlexContainer";

export type FlexItemProps = HTMLAttributes<HTMLDivElement> & {
  asChild?: boolean;
  grow?: number;
  shrink?: number;
  align?: "start" | "center" | "end";
  justify?: "start" | "center" | "end";
};

export const Item = forwardRef<HTMLDivElement, FlexItemProps>(
  (
    {
      asChild = false,
      grow,
      shrink,
      align,
      justify,
      style,
      className,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "div";
    const applyShrink = typeof shrink === "number";

    const inlineStyle = {
      ...style,
      ...(grow ? { "--flex-item-grow": grow } : null),
      ...(applyShrink ? { "--flex-item-shrink": shrink } : null),
    };

    return (
      <Comp
        ref={ref}
        className={clsx(
          grow && classes.itemGrow,
          applyShrink && classes.itemShrink,
          align && classes[`itemAlign-${align}`],
          justify && classes[`itemJustify-${justify}`],
          className,
        )}
        style={inlineStyle}
        {...props}
      />
    );
  },
);

Item.displayName = "FlexItem";
